import {gql} from '@apollo/client';

export const GET_INSTITUTIONAL_ACCOUNTS = gql`
  query GetInstitutionalAccounts($filter: InstitutionalAccountFilter) {
    getInstitutionalAccounts(filter: $filter) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstitutionalAccounts {
        message
        data {
          id
          entityName
          registrationNumber
          countryOfIncorporation
          businessEmail
          kybStatus
          kybSubmittedAt
          kybReviewedAt
          duplicateRegistrationFlag
          isFullyActivated
          status
          createdAt
          primaryContact {
            id
            firstName
            lastName
            email
          }
          kybReviewer {
            id
            firstName
            lastName
          }
        }
        pagination {
          page
          limit
          pages
          total
          hasNextPage
          hasPrevPage
          next
          previous
        }
      }
    }
  }
`;

/**
 * Counts for the queue tabs.
 *
 * The API has no per-status count, so each badge is one filtered call with
 * `limit: 1` read for `pagination.total`. Deliberately a thin document: it is
 * fetched once per status and none of the rows are rendered.
 */
export const COUNT_INSTITUTIONAL_ACCOUNTS = gql`
  query CountInstitutionalAccounts($filter: InstitutionalAccountFilter) {
    getInstitutionalAccounts(filter: $filter) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstitutionalAccounts {
        pagination {
          total
        }
      }
    }
  }
`;

export const GET_INSTITUTIONAL_ACCOUNT_BY_ID = gql`
  query GetInstitutionalAccountById($id: ID!) {
    getInstitutionalAccountById(id: $id) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstitutionalAccount {
        message
        data {
          id
          entityName
          registrationNumber
          countryOfIncorporation
          businessEmail
          businessPhone
          businessDescription
          website
          administratorNationality

          contactAuthorityBasis
          hasNoMajorityOwner
          primaryContact {
            id
            firstName
            lastName
            email
            phoneNumber
          }
          beneficialOwners {
            id
            fullName
            ownershipPct
            isPrimaryContact
            nationality
            idDocument {
              id
              fileName
              fileUrl
              status
            }
          }

          kybStatus
          kybSubmittedAt
          kybReviewedAt
          kybReviewReason
          kybReviewer {
            id
            firstName
            lastName
          }
          duplicateRegistrationFlag

          status
          isFullyActivated
          activatedAt

          documents {
            id
            documentType
            freeName
            beneficialOwnerId
            fileName
            fileUrl
            status
            reviewerNote
            uploadedBy {
              id
              firstName
              lastName
            }
            reviewedBy {
              id
              firstName
              lastName
            }
            reviewedAt
            createdAt
          }

          kybRequirements {
            key
            documentType
            label
            mandatory
            condition
            satisfied
            satisfiedByContactKyc
            beneficialOwnerId
            beneficialOwnerName
            documentIds
          }

          statusHistory {
            from
            to
            actorId
            reason
            at
          }

          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const SET_KYB_STATUS = gql`
  mutation SetKybStatus($input: SetKybStatusInput!) {
    setKybStatus(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstitutionalAccount {
        message
        data {
          id
          kybStatus
          kybReviewReason
          kybReviewedAt
          status
          isFullyActivated
          activatedAt
          kybReviewer {
            id
            firstName
            lastName
          }
          statusHistory {
            from
            to
            actorId
            reason
            at
          }
        }
      }
    }
  }
`;

/**
 * Submits the account for review on the business's behalf.
 *
 * The same operation the business calls from the web app. `submitted: false`
 * is not an error: the checklist is not complete, and `missingRequirements`
 * says what is outstanding. On success the business gets the usual
 * "submission received" email.
 */
export const SUBMIT_KYB_FOR_REVIEW = gql`
  mutation SubmitKybForReview($accountId: ID!) {
    submitKybForReview(accountId: $accountId) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithKybSubmission {
        message
        data {
          submitted
          account {
            id
            kybStatus
          }
          missingRequirements {
            key
            label
            documentType
            beneficialOwnerName
          }
        }
      }
    }
  }
`;
